import type { CompanySectionsState } from "@/contexts/company-sections-context";
import { getSelectedTeamId, useApi } from "@/hooks/useApi";
import {
  COMPANY_TAB_READ_QUERY_OPTIONS,
  companyTabReads,
} from "@/lib/company-tab-reads";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";

type CompanyTabReadName = keyof ReturnType<typeof companyTabReads>;

/** What the tab triggers call with their tab's value. */
export type CompanyTabIntentHandlers = {
  /** The user reached for the tab: a held hover, the focus, a click. */
  onTabIntent: (tab: string) => void;
  /** The pointer left the tab's trigger: the reads not asked yet are not. */
  onTabIntentEnd: (tab: string) => void;
};

/** The tab whose reads are prefetched — the reads of `companyTabReads`. */
const PREFETCHED_TAB = "news";

/**
 * Prefetches the news tab's reads into the query cache once the user shows
 * they are about to open it — the pointer held over its trigger, or the focus
 * on it — so the tab reads the cache instead of waiting on three requests.
 * Nothing is asked without that gesture, and the gesture lasts while the
 * pointer stays on the trigger and on this page only: each read can reach a
 * paid provider (up to four Mantiks credits for the job offers), and most
 * pages are closed without the tab ever being opened.
 *
 * Even then a read waits for this run's snapshot and for its sections to
 * settle — never while the run still enriches them, which would ask the same
 * providers twice, nor when the snapshot does not hold them: a section this
 * run does not enrich is not settled, and its read would ask the providers
 * itself.
 *
 * Each read is asked once per page: every read counts against the team's
 * daily tab reads, and one refused (`retry: false` caches nothing) would be
 * asked again each time another section settles.
 *
 * Returns the callbacks the tab triggers call with their tab's value.
 */
export function usePrefetchCompanyTabs(
  companyId: string,
  hasSnapshot: boolean,
  sections: CompanySectionsState["sections"],
): CompanyTabIntentHandlers {
  const api = useApi();
  const queryClient = useQueryClient();
  const asked = useRef(new Set<string>());

  const teamId = getSelectedTeamId();
  const page = `${teamId}|${companyId}`;
  // The page the gesture was made on: another page starts without one, and
  // so does the same page opened again (A, then B, then A).
  const [wantedOn, setWantedOn] = useState<string | null>(null);
  const [shownPage, setShownPage] = useState(page);
  if (shownPage !== page) {
    setShownPage(page);
    setWantedOn(null);
  }
  const isWanted = wantedOn === page;

  const reads = companyTabReads(teamId, companyId);
  // A string, so the effect runs again only when another read becomes ready.
  const readyReads = (Object.keys(reads) as CompanyTabReadName[])
    .filter((name) =>
      reads[name].sections.every(
        (key) => key in sections && sections[key] !== "pending",
      ),
    )
    .join(",");

  // `api` is a new object on each render: read the latest one.
  const prefetch = useEffectEvent((names: readonly CompanyTabReadName[]) => {
    for (const name of names) {
      const { key, path } = reads[name];
      const askedKey = JSON.stringify(key);
      if (asked.current.has(askedKey)) continue;
      asked.current.add(askedKey);
      void queryClient.prefetchQuery({
        queryKey: key,
        queryFn: async () => (await api.get<unknown>(path)).data,
        ...COMPANY_TAB_READ_QUERY_OPTIONS,
      });
    }
  });

  useEffect(() => {
    if (!companyId || !isWanted || !hasSnapshot || !readyReads) return;
    prefetch(readyReads.split(",") as CompanyTabReadName[]);
  }, [teamId, companyId, isWanted, hasSnapshot, readyReads]);

  const onTabIntent = useCallback(
    (tab: string) => {
      if (tab === PREFETCHED_TAB) setWantedOn(page);
    },
    [page],
  );
  const onTabIntentEnd = useCallback((tab: string) => {
    if (tab === PREFETCHED_TAB) setWantedOn(null);
  }, []);
  return { onTabIntent, onTabIntentEnd };
}
